import React, { useState, useEffect, useRef } from 'react';
import { TestimonialCard } from './TestimonialCard';
import { trackButtonClick } from '../../firebaseConfig';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';
import { Star, Quote } from 'lucide-react';

export function Testimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  const { trackSectionView } = useAnalyticsTracking();
  
  // Set up our own intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Staggered animation sequence
          setTimeout(() => setAnimationPhase(1), 100); // Section title
          setTimeout(() => setAnimationPhase(2), 400); // Description
          setTimeout(() => setAnimationPhase(3), 700); // First testimonial row
          setTimeout(() => setAnimationPhase(4), 1000); // Second testimonial row
          
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Track when section becomes visible
  useEffect(() => {
    if (isVisible) {
      trackButtonClick('Section Viewed', 'Testimonials Section');
      trackSectionView('Testimonials Section', {
        section_name: 'testimonials',
        section_position: 'bottom',
        visibility_percentage: Math.round(0.2 * 100)
      });
    }
  }, [isVisible, trackSectionView]);

  // Track interactions with testimonial cards
  const handleTestimonialClick = (name: string) => {
    trackButtonClick(`Testimonial: ${name}`, 'Testimonials Section');
  };

  const testimonials = [
    {
      name: "Sumon Roy",
      designation: "Software Development Engineer II",
      organization: "Swiggy",
      image: "/testimonies/sumon-testimony.jpeg",
      content: "MockTrail was a <strong>game-changer</strong> in my interview prep journey. The platform <strong>gave me a clear edge</strong> — from tailoring mock interviews based on real job descriptions to practicing behavioral and system design rounds with AI-powered feedback.",
      gradient: "from-blue-500/10 via-purple-500/10 to-blue-500/10"
    },
    {
      name: "Subham Singh",
      designation: "Final Year CSE Student",
      organization: "KIIT, Bhubaneswar", 
      image: "/testimonies/subham-testimony.jpg",
      content: "MockTrail really helped me overcome my <strong>interview anxiety</strong>. The realistic practice sessions boosted my confidence and sharpened my communication skills. Thanks to this platform, I was able to <em>crack three internships</em>.",
      gradient: "from-emerald-500/10 via-teal-500/10 to-emerald-500/10"
    },
    {
      name: "Bhaskar Kaura",
      designation: "Software Engineer",
      organization: "Compunnel Inc.",
      // No image provided - will show initials "BK"
      content: "MockTrail's <em>behavioral analysis</em> helped me understand my strengths and weaknesses in detail. The AI-powered feedback system provided insights I never got elsewhere. The <strong>improvement in my interview performance</strong> was remarkable.",
      gradient: "from-orange-500/10 via-amber-500/10 to-orange-500/10"
    },
    {
      name: "Tamanna Gandhi",
      designation: "CSE Student", 
      organization: "Lovely Professional University",
      image: "/testimonies/tamanna-testimony.png",
      content: "Using MockTrail has been a <strong>fantastic experience</strong>. As a second-year Computer Science student, I found the platform extremely helpful in improving my <em>confidence and public speaking skills</em>. The website provided realistic interview practice.",
      gradient: "from-rose-500/10 via-pink-500/10 to-rose-500/10"
    },
    {
      name: "Abhishek Nimbalkar",
      designation: "Backend Engineer",
      organization: "Antino Labs",
      image: '/testimonies/abhishek-testimony.jpeg',
      content: "MockTrail's <strong>AI-powered mock interviews</strong> were a game-changer for me. The platform helped me practice <em>technical and behavioral questions</em> effectively. I felt much more <strong>confident during real interviews</strong> after using this platform.",
      gradient: "from-violet-500/10 via-purple-500/10 to-violet-500/10"
    },
    {
      name: "Gajanan Palepwad",
      designation: "Application & AI Engineer",
      organization: "Boomlex Private",
      // No image provided - will show initials "GP"
      content: "The <strong>AI interview trainer</strong> provided invaluable real-time feedback on my communication style. It's like having a <em>personal interview coach available 24/7</em>, leading to a <strong>significant boost in confidence and performance</strong>.",
      gradient: "from-blue-500/10 via-purple-500/10 to-blue-500/10"
    },
    {
      name: "Utkarsh Saxena",
      designation: "Android Developer",
      organization: "Workplace, Ex: Groww",
      // No image provided - will show initials "PS"
      content: "The <strong>personalized feedback system</strong> in MockTrail is incredible. It identified my weak areas and provided <em>actionable insights</em> to improve. The mock interviews felt so <strong>realistic and professional</strong>. Super product by team at MockTrail!",
      gradient: "from-cyan-500/10 via-blue-500/10 to-cyan-500/10"
    },
    {
      name: "Akshay Sharma",
      designation: "Business Analyst",
      organization: "WIL",
      // No image provided - will show initials "RM"
      content: "MockTrail's <strong>industry-specific questions</strong> helped me prepare for tech interviews brilliantly. The AI coach provided <em>detailed performance analytics</em> that helped me track my <strong>progress effectively</strong>. It's pricing is also very reasonable.",
      gradient: "from-amber-500/10 via-yellow-500/10 to-amber-500/10"
    },
    {
      name: "Atul Mishra",
      designation: "Lead Engineer",
      organization: "Pantheon Digital",
      image: '/testimonies/atul-testimony.jpeg',
      content: "The <strong>mock interview experience</strong> on MockTrail closely mimics real interviews. The platform helped me overcome my <em>nervousness and improve articulation</em>. Highly recommend for <strong>interview preparation</strong>. Great work by team at MockTrail!",
      gradient: "from-green-500/10 via-emerald-500/10 to-green-500/10"
    },
  ];

  return (
    <section 
      ref={sectionRef}
      id="testimonials" 
      className="py-32 -mb-24 md:-mb-16 relative overflow-hidden"
    >
      {/* Enhanced Background Effects */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-1000" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-1000 delay-200" 
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-1000 delay-300" 
        data-speed="0.08"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      {/* Decorative elements */}
      <div 
        className="absolute left-[10%] top-[20%] w-40 h-40 rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-xl parallax-element opacity-0 transition-opacity duration-1000 delay-300" 
        data-speed="0.12"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute right-[10%] bottom-[30%] w-48 h-48 rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-xl parallax-element opacity-0 transition-opacity duration-1000 delay-400" 
        data-speed="0.09"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      {/* Floating quote marks */}
      <div 
        className="absolute left-[5%] top-[30%] text-[120px] leading-none font-serif text-primary-light/10 dark:text-primary-dark/10 parallax-element opacity-0 transition-opacity duration-1000 delay-600" 
        data-speed="0.15"
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        "
      </div>
      <div 
        className="absolute right-[5%] bottom-[25%] text-[120px] leading-none font-serif text-secondary-light/10 dark:text-secondary-dark/10 parallax-element opacity-0 transition-opacity duration-1000 delay-700" 
        data-speed="0.18"
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        "
      </div>

      <div className="relative">
        {/* Centered header section */}
        <div className="container mx-auto px-4 text-center mb-2">
          <h2 
            className={`text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
           Loved By Many - Who're Using MockTrail
          </h2>
          <p 
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Join Countless people who have transformed their interview preparation with MockTrail
          </p>
        </div>

        {/* Full-width testimonials slider with breathing room for glow effects */}
        <div className="relative overflow-hidden w-full py-12">
          {/* First Row */}
          <div 
            className={`flex gap-3 animate-scroll-left md:animate-scroll-left transform transition-all duration-1200 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ 
              animationPlayState: isVisible ? 'running' : 'paused'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.animationPlayState = 'paused';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.animationPlayState = 'running';
            }}
          >
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <div 
                key={index} 
                className="w-[370px] md:w-[510px] flex-shrink-0 transform-gpu transition-transform duration-200 ease-out will-change-transform py-6 px-2"
                style={{ 
                  backfaceVisibility: 'hidden',
                  perspective: '1000px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1.02, 1.02, 1) translateZ(0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1, 1, 1) translateZ(0)';
                }}
                onClick={() => handleTestimonialClick(testimonial.name)}
              >
                <TestimonialCard {...testimonial} />
              </div>
            ))}
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <div 
                key={`repeat-${index}`} 
                className="w-[370px] md:w-[510px] flex-shrink-0 transform-gpu transition-transform duration-200 ease-out will-change-transform py-6 px-2"
                style={{ 
                  backfaceVisibility: 'hidden',
                  perspective: '1000px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1.02, 1.02, 1) translateZ(0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1, 1, 1) translateZ(0)';
                }}
                onClick={() => handleTestimonialClick(testimonial.name)}
              >
                <TestimonialCard {...testimonial} />
              </div>
            ))}
          </div>

          {/* Second Row */}
          <div 
            className={`flex gap-3 -mt-4 animate-scroll-right md:animate-scroll-right transform transition-all duration-1200 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              animationPhase >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ 
              animationPlayState: isVisible ? 'running' : 'paused',
              transitionDelay: '0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.animationPlayState = 'paused';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.animationPlayState = 'running';
            }}
          >
            {testimonials.slice(4).map((testimonial, index) => (
              <div 
                key={index} 
                className="w-[370px] md:w-[510px] flex-shrink-0 transform-gpu transition-transform duration-200 ease-out will-change-transform py-6 px-2"
                style={{ 
                  backfaceVisibility: 'hidden',
                  perspective: '1000px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1.02, 1.02, 1) translateZ(0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1, 1, 1) translateZ(0)';
                }}
                onClick={() => handleTestimonialClick(testimonial.name)}
              >
                <TestimonialCard {...testimonial} />
              </div>
            ))}
            {testimonials.slice(4).map((testimonial, index) => (
              <div 
                key={`repeat-${index}`} 
                className="w-[370px] md:w-[510px] flex-shrink-0 transform-gpu transition-transform duration-200 ease-out will-change-transform py-6 px-2"
                style={{ 
                  backfaceVisibility: 'hidden',
                  perspective: '1000px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1.02, 1.02, 1) translateZ(0)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale3d(1, 1, 1) translateZ(0)';
                }}
                onClick={() => handleTestimonialClick(testimonial.name)}
              >
                <TestimonialCard {...testimonial} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}